// Edit planner for security ops — logins / users / roles (v2 Slice F1).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice F1.
//
// All nine ops append a fresh statement after the source statement (DSE2-7:
// no coalescing by default). Per-engine support / rejection lives in the
// dialect synthesizers — `dialect.login.ts`, `dialect.user.ts`, `dialect.role.ts`.

import { rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import type { DdlEdit, PlannedByteEdit } from '../../types';

import { pickAppendInsertion } from './appendInsertion';
import type { DdlEditCtx } from './applyDdlEdit';
import {
  synthesizeAlterLogin,
  synthesizeCreateLogin,
  synthesizeDropLogin,
} from './dialect.login';
import {
  synthesizeAlterRole,
  synthesizeCreateRole,
  synthesizeDropRole,
} from './dialect.role';
import {
  synthesizeAlterUser,
  synthesizeCreateUser,
  synthesizeDropUser,
} from './dialect.user';

type SecurityEdit = Extract<
  DdlEdit,
  {
    op:
      | 'create-login'
      | 'alter-login'
      | 'drop-login'
      | 'create-user'
      | 'alter-user'
      | 'drop-user'
      | 'create-role'
      | 'alter-role'
      | 'drop-role';
  }
>;

export function planSecurityEdit(
  ctx: DdlEditCtx,
  edit: SecurityEdit,
): readonly PlannedByteEdit[] {
  const synth = synthForSecurity(ctx, edit);
  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(
    ctx.source,
    ctx.statementInfo.range.endOffset,
  );
  return [{
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: sep + synth + trailSep,
    rationale: edit.op,
  }];
}

function synthForSecurity(ctx: DdlEditCtx, edit: SecurityEdit): string {
  switch (edit.op) {
    case 'create-login':
      return synthesizeCreateLogin(ctx.engine, edit.login);
    case 'alter-login':
      return synthesizeAlterLogin(
        ctx.engine,
        edit.loginName,
        edit.quoted ?? false,
        edit.change,
      );
    case 'drop-login':
      return synthesizeDropLogin(
        ctx.engine,
        edit.loginName,
        edit.quoted ?? false,
        edit.ifExists ?? false,
      );
    case 'create-user':
      return synthesizeCreateUser(ctx.engine, edit.user, ctx.mysqlNoBackslashEscapes);
    case 'alter-user':
      return synthesizeAlterUser(
        ctx.engine,
        edit.userName,
        edit.quoted ?? false,
        edit.mysqlHost,
        edit.change,
        ctx.mysqlNoBackslashEscapes,
      );
    case 'drop-user':
      return synthesizeDropUser(
        ctx.engine,
        edit.userName,
        edit.quoted ?? false,
        edit.ifExists ?? false,
        edit.mysqlHost,
      );
    case 'create-role':
      return synthesizeCreateRole(ctx.engine, edit.role);
    case 'alter-role':
      return synthesizeAlterRole(
        ctx.engine,
        edit.roleName,
        edit.quoted ?? false,
        edit.mysqlHost,
        edit.change,
      );
    case 'drop-role':
      return synthesizeDropRole(
        ctx.engine,
        edit.roleName,
        edit.quoted ?? false,
        edit.ifExists ?? false,
        edit.mysqlHost,
      );
  }
}
